import { useEffect, useState } from 'react';
import { useLang } from '../lib/i18n';
import { withLangPrefix } from '../lib/i18n-core';

const STORAGE_KEY = 'popwiz_cookie_consent_v1';

type Consent = 'accepted' | 'rejected';

function readConsent(): Consent | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'rejected' ? v : null;
  } catch {
    return null;
  }
}

function writeConsent(v: Consent) {
  try {
    localStorage.setItem(STORAGE_KEY, v);
  } catch {}
}

export default function CookieBanner() {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (readConsent() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  const copy = lang === 'zh'
    ? {
        title: '我们使用 Cookie',
        body: '我们使用必要 Cookie 保持登录状态,并使用分析 Cookie 改进服务。查看我们的',
        privacyLink: '隐私政策',
        accept: '接受全部',
        reject: '仅必要',
      }
    : {
        title: 'We use cookies',
        body: 'We use essential cookies to keep you signed in and analytics cookies to improve the service. See our',
        privacyLink: 'privacy policy',
        accept: 'Accept all',
        reject: 'Essential only',
      };

  const decide = (v: Consent) => {
    writeConsent(v);
    setVisible(false);
  };

  const privacyHref = withLangPrefix('/app/privacy', lang);

  return (
    <div
      role="dialog"
      aria-label={copy.title}
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-border shadow-2xl"
    >
      <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1 text-sm text-txt-primary">
          <span className="font-semibold mr-1">{copy.title}.</span>
          <span className="text-txt-secondary">
            {copy.body}{' '}
            <a href={privacyHref} className="underline text-brand-orange">
              {copy.privacyLink}
            </a>
            .
          </span>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => decide('rejected')}
            className="text-xs px-3 py-1.5 rounded-lg border border-border text-txt-secondary hover:bg-surface-hover"
          >
            {copy.reject}
          </button>
          <button
            type="button"
            onClick={() => decide('accepted')}
            className="text-xs px-3 py-1.5 rounded-lg bg-brand-orange text-white font-medium hover:opacity-90"
          >
            {copy.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
